import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import {
  setSelectedBusinessName,
  setSelectedLocationId,
  setSelectedAccountId,
} from 'src/redux/localSeo';
import {
  setLocalSeoId,
  setLocalSeoBusinessId,
} from 'src/common/config/FunctionHelperPartTwo';
import DropdownPopover from 'src/components/UI/DropdownPopover';
import ReviewAutomationBreadcrumbs from './ReviewAutomationBreadcrumbs';
import { useGetUserAgentDetails } from '../../hooks/useGetUserAgentDetails';
import { getProfileDisabledState } from '../../hooks/useIsConfiguredToOtherSite';
import useSiteGBP1to1Mapping, { IGBPLocation } from '../../hooks/useSiteGBP1to1Maping';

interface ReviewAutomationCommonProps {
  onSelect?: (profile: IGBPLocation) => void;
  selectedProfile?: IGBPLocation | null;
}

function ReviewAutomationCommon({ onSelect, selectedProfile: propSelectedProfile }: ReviewAutomationCommonProps) {
  const dispatch = useDispatch();

  const { _id: siteId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};

  const siteGBP1to1Configurations = useSiteGBP1to1Mapping({ siteId });

  const { data: userAgentDetails } = useGetUserAgentDetails(siteId);
  const localSeoApiData = userAgentDetails?.response?.local_seo;
  const reviewAutomationApiData = userAgentDetails?.response?.review_automation;

  const isOptionDisabled = (profile: any) =>
    getProfileDisabledState(profile, localSeoApiData, siteId, siteGBP1to1Configurations.connectedSite?.url, siteGBP1to1Configurations.siteList, reviewAutomationApiData).disabled;

  const getOptionDisabledTooltip = (profile: any) =>
    getProfileDisabledState(profile, localSeoApiData, siteId, siteGBP1to1Configurations.connectedSite?.url, siteGBP1to1Configurations.siteList, reviewAutomationApiData).tooltip;

  const effectiveSelectedProfile = useMemo(() => {
    if (propSelectedProfile) return propSelectedProfile;
    if (!siteGBP1to1Configurations || siteGBP1to1Configurations.isConfigurationError) return null;
    return siteGBP1to1Configurations.connectedGBPAccount ?? null;
  }, [propSelectedProfile, siteGBP1to1Configurations]);

  useEffect(() => {
    if (onSelect && effectiveSelectedProfile && !propSelectedProfile) {
      onSelect(effectiveSelectedProfile);
    }
  }, [effectiveSelectedProfile, onSelect, propSelectedProfile]);

  const handleProfileSelect = (location: IGBPLocation) => {
    if (onSelect) {
      onSelect(location);
    }
  };

  return (
    <div className="flex justify-between items-center mb-3 w-full">
      <div>
        <ReviewAutomationBreadcrumbs />
      </div>
      {siteGBP1to1Configurations.GBPLocations?.length > 0 && (
        <div className="flex gap-2 items-center">
          {/* Google Business Profile Selector */}
          <div aria-label="google-business-profile" style={{ minWidth: 200 }}>
            <DropdownPopover
              startIcon="/static/images/icons/local-seo/google-home.svg"
              selectedValue={effectiveSelectedProfile}
              options={siteGBP1to1Configurations.GBPLocations || []}
              onSelect={handleProfileSelect}
              isArrayOfObject={true}
              labelKey="title"
              idKey="name"
              placeholder="Select Business"
              enableSearch={siteGBP1to1Configurations.GBPLocations?.length > 5}
              searchPlaceholder="Search profiles..."
              isOptionDisabled={isOptionDisabled}
              getOptionDisabledTooltip={getOptionDisabledTooltip}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewAutomationCommon;
